import { EVENT } from "@/constants/event";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import useRequest from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { useEffect, useMemo, useRef, useState } from "react";
import NewMessage from "../../components/NewMessage";
import PinList from "../../components/PinList";

import {
  getAds,
  getDeliveryDetail,
  getDiscoveryRecommend,
} from "../../services";

import { AD_POSITION } from "@/constants/ads";
import { Categories } from "@/constants/categories";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import useAppStore from "@/context/store";
import { filterAndSortAiGameplayList } from "@/utils/aiGameplayExposure";
import useCookieState from "@/utils/useCookieState";
import dayjs from "dayjs";
import Head from "next/head";

// 第二天6点cookie失效
function getExpireDate() {
  const now = dayjs();
  let date;
  if (now.hour() > 6) {
    date = dayjs().add(1, "day").format("YYYY-MM-DD ");
  } else {
    date = dayjs().format("YYYY-MM-DD");
  }

  return dayjs(date + "06:00:00").toDate();
}

export default function DiscoveryFeaturePinList({
  categoryId,
  featuredId,
  sortName,
  recommends,
  initList,
  aiGameplayList = [],
}) {
  // 会员判断：会员显示版权标识，非会员不显示
  const isVip = useIsMaterialVip();
  const { user, column: columnNum } = useAppStore((s) => ({
    user: s.user,
    column: s.column,
  }));

  const showMessageRef = useRef(true);
  const randomNumRef = useRef(null);
  const queryRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(initList || []);
  const [prevDataSource, setPrevDataSource] = useState([]);
  const [ads, setAds] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);
  const initRef = useRef(!initList);
  const prevUserIdRef = useRef(user?.user_id);

  // 一次性过滤和排序AI列表，在当前会话期间保持不变
  // 避免滚动加载时因曝光次数变化导致列表抖动
  const stableAiGameplayList = useMemo(() => {
    return filterAndSortAiGameplayList(aiGameplayList);
  }, [aiGameplayList]);
  const [cachePage, setCachePage] = useCookieState("feature_page", {
    defaultValue: "{}",
    expires: getExpireDate(),
    // path: "/discovery", 非当前页刷新会写不进去cookie
    sameSite: true,
  });

  const [page, setPage] = useState({
    current: cachePage[categoryId || "all"] || 1,
  });

  const cacheNextPage = useMemoizedFn(() => {
    setCachePage({
      ...cachePage,
      [categoryId || "all"]: page.current + 1,
    });
  });
  useEffect(() => {
    cacheNextPage();
  }, [cacheNextPage, page]);

  useRequest(
    async () => {
      if (!initRef.current) {
        // 如果服务端渲染进来之后初次不用执行
        initRef.current = true;
        return;
      }

      const category = categoryId === "all" ? null : categoryId;

      const [result, waterfallAd] = await Promise.all([
        getDiscoveryRecommend({
          page_num: page.current,
          page_size: 40,
          ...(!category ? { position: "discovery_recommend" } : null),
          category,
        }),
        dataSource.length > 1 // 第一页不展示广告，故不请求
          ? getAds({
              positions: [AD_POSITION.DISCOVERY_WATERFALL_PER_PAGE],
              variables: {
                user_tag: ["pin"],
                category: [categoryId || "none"],
              },
            })
          : null,
      ]);

      if (queryRef.current !== categoryId) {
        return;
      }

      const pins = result?.pins || [];

      if (waterfallAd?.[0] && pins.length > 0) {
        //追加到列表前
        pins.unshift({
          ...waterfallAd[0],
          pin_id: `recommend-${waterfallAd[0].material.id}-${page.current}`,
          type: "recommend",
        });
      }

      if (pins.length <= 0) {
        if (page.current !== 1) {
          // 到底部从头开始
          setPage({ current: 1 });
        } else {
          setHasMore(false);
        }
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [page],
    }
  );

  useRequest(
    async () => {
      const result = await getAds({
        positions: [44, 45, 46, 55],
        variables: {
          user_tag: ["pin"],
          category: [categoryId || "none"],
        },
      });
      setAds(
        result?.reduce((result, item) => {
          if (item) {
            result.push({
              ...item,
              pin_id: `recommend-${item.material.id}`,
              type: "recommend",
            });
          } else {
            result.push(null);
          }
          return result;
        }, []) || []
      );

      if (!categoryId || categoryId === "all") {
        const result = await getDeliveryDetail("web-discover-feedback");
        setQuestionaire(
          result?.pit_modules?.[0]?.material
            ? {
                type: RENDERCARDTYPE.questionaire,
                pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
                id: result?.pit_modules?.[0]?.module_id,
                name: result?.pit_modules?.[0]?.module_name,
                ...result?.pit_modules?.[0]?.material,
              }
            : null
        );
      }
    },
    {
      refreshDeps: [categoryId],
    }
  );

  const onMergeDataSurce = () => {
    window.scrollTo(0, 0);

    setTimeout(() => {
      // 延迟，临时处理 @城管
      setDataSource(prevDataSource.concat(dataSource));
      setPrevDataSource([]);
    }, 200);
  };

  const onLoadMore = () => {
    if (dataSource.length > 0) {
      setPage({ current: page.current + 1 });
    }
  };

  const initFlag = useRef(true);
  const onChangeCategoryId = useMemoizedFn(() => {
    setPage({ current: cachePage[categoryId || "all"] || 1 });
    setHasMore(true);
    setPrevDataSource([]);
    setDataSource([]);
  });
  useEffect(() => {
    // 因为后端数据返回很慢，所以做个判断，当条件不一致的时候就不setDataSource了
    queryRef.current = categoryId;
    if (initFlag.current) {
      initFlag.current = false;
      return;
    }
    onChangeCategoryId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // 监听用户登录状态变化，登录后刷新列表
  useEffect(() => {
    const currentUserId = user?.user_id;
    const prevUserId = prevUserIdRef.current;

    // 从未登录变为登录状态时，刷新列表
    if (!prevUserId && currentUserId) {
      setDataSource([]);
      setPrevDataSource([]);
      setPage({ current: 1 });
      setHasMore(true);
    }

    // 更新 ref
    prevUserIdRef.current = currentUserId;
  }, [user?.user_id]);

  // 重新加载发现瀑布流
  useEffect(() => {
    const onReload = () => {
      setDataSource([]);
      setPrevDataSource([]);
      setPage({ current: page.current + 1 });
    };

    window.addEventListener(EVENT.DISCOVERY_PIN_LIST_RESET, onReload);

    return () => {
      window.removeEventListener(EVENT.DISCOVERY_PIN_LIST_RESET, onReload);
    };
  }, [setDataSource, page]);

  const list = useMemo(() => {
    if (dataSource.length <= 0 || (ads.length <= 0 && !questionaire)) {
      if (recommends?.length > 0) {
        return [...recommends, ...dataSource];
      }
      return dataSource;
    }

    if (randomNumRef.current === null) {
      randomNumRef.current = parseInt(Math.random() * 10) + 1;
    }

    const num = randomNumRef.current;

    const list = [
      ...(!featuredId ? ads.slice(0, 1).filter((i) => i) : []),
      ...dataSource.slice(0, num),
    ];

    const newList1 = [
      ...(recommends || []),
      ...list.slice(0, 1),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(1),
      ...ads.slice(2, 3).filter((i) => i),
      ...dataSource.slice(num),
    ];

    const newList2 = [
      ...newList1.slice(0, 3),
      ...ads.slice(3, 4).filter((i) => i),
      ...newList1.slice(3),
    ];

    if (newList2.length > 60 && questionaire) {
      newList2.splice(60, 0, questionaire);
    }

    // 插入AI玩法资源位：轮换队列 + 动态间隔
    if (stableAiGameplayList.length > 0 && newList2.length > 0) {
      // 使用稳定的AI列表，在当前会话期间不会因曝光次数变化而改变
      const availableAiList = stableAiGameplayList;

      if (availableAiList.length > 0) {
        // 2. 根据列数计算插入间隔
        const getInsertInterval = (colNum) => {
          if (colNum <= 5) return 20;
          if (colNum === 6) return 30;
          if (colNum >= 7 && colNum <= 8) return 40;
          if (colNum >= 9) return 60;
          return 20; // 默认值
        };

        const interval = getInsertInterval(columnNum || 5);
        const positions = [];
        let currentPos = 4; // 第一个位置（索引4，即第5个）

        // 3. 计算所有插入位置
        for (let i = 0; i < availableAiList.length; i++) {
          // 兜底：确保采集内容充足，AI卡片占比不超过总数的20%
          // 即：每插入1个AI卡片，至少要有4个采集内容
          const minRequiredContent = (i + 1) * 4;

          if (
            currentPos < newList2.length &&
            newList2.length >= minRequiredContent
          ) {
            positions.push({ pos: currentPos, aiIndex: i });
            currentPos += interval; // 根据列数动态调整间隔
          } else {
            // 采集内容不足以支撑更多AI卡片，停止插入
            break;
          }
        }

        // 4. 从后往前插入，避免索引偏移问题
        positions.reverse().forEach(({ pos, aiIndex }) => {
          const aiItem = availableAiList[aiIndex];
          newList2.splice(pos, 0, {
            ...aiItem,
            pin_id: `ai-gameplay-${aiItem.id}-${pos}`,
            type: "aiGameplay",
            location: aiIndex,
            actualLocation: pos + 1, // 记录在整个列表中的实际位置（从1开始）
          });
        });
      }
    }

    return newList2;
  }, [
    dataSource,
    ads,
    questionaire,
    featuredId,
    recommends,
    stableAiGameplayList,
    columnNum,
  ]);

  const categoryName = useMemo(() => {
    return (
      Categories.find((category) => category.value === categoryId)?.label ||
      "最新"
    );
  }, [categoryId]);

  return (
    <>
      <Head>
        <title>
          {categoryName}图片采集_{categoryName}素材采集-花瓣网
        </title>
        <meta
          name="keywords"
          content={`${categoryName},${categoryName}图片,${categoryName}素材,${categoryName}资源`}
        />
        <meta
          name="description"
          content={`花瓣网${categoryName}采集栏目为推荐了${categoryName}采集相关的图片素材资源，其中涵盖众多设计师收藏采集的相关图片素材资源。花瓣网, 设计师寻找灵感的天堂！`}
        />
      </Head>
      <NewMessage
        onClick={onMergeDataSurce}
        onClose={() => (showMessageRef.current = false)}
        length={prevDataSource.length}
        title="采集"
      />
      <PinList
        dataSource={list}
        hasMore={hasMore}
        isSearch={true}
        onLoadMore={onLoadMore}
        pageSize={40}
        showMaterialTag={isVip}
      />
    </>
  );
}
